/* eslint-disable camelcase */
import { useMemo } from "react";
import styled from "@emotion/styled";
import PropTypes from "prop-types";

import { useFollowUser, useOneElastic } from "@hmn/coolinarika-web-core/hooks";
import { trophiesEnabled } from "@hmn/coolinarika-web-core/settings/global/flag-config";

import { Button, buttonSizes, buttonVariants } from "../../../Button";
import { withErrorBoundary } from "../../../ErrorBoundary";
import { UserProfileAvatar } from "./Avatar";
import { UserProfileContent } from "./Content";
import { UserProfileTabs } from "./Tabs";
import { UserProfileTrophy } from "./Trophy";
import styles from "./UserProfileHead.style";

const UserProfileHeadStyled = styled.div((props) => ({ ...styles(props) }));

const UserProfileHead = ({
    className,
    userId,
    label,
    username,
    title,
    text,
    lead,
    hasTrophiesList,
    trophiesLink,
    ...rest
}) => {
    const { buttonTitle, handleFollowUser } = useFollowUser(userId);

    const { data: achievements } = useOneElastic({
        namespace: "gamification",
        resource: "achievements",
        id: userId,
        enabled: !!userId,
    });
    const { message: trophyAwards } = achievements || {};

    const activeTrophyAwards = useMemo(
        () => trophyAwards?.filter((trophy) => trophy.awarded) || [],
        [trophyAwards],
    );

    const tabs = useMemo(
        () =>
            [
                {
                    id: "pocetna",
                    title: "Početna",
                    href: "/profil/[username]",
                    as: `/profil/${username}`,
                },
                {
                    id: "o-meni",
                    title: "O meni",
                    href: "/profil/[username]/o-meni",
                    as: `/profil/${username}/o-meni`,
                },
                trophiesEnabled &&
                    activeTrophyAwards.length > 0 && {
                        id: "trofeji",
                        title: "Trofeji",
                        href: "/profil/[username]/moji-trofeji",
                        as: `/profil/${username}/moji-trofeji`,
                    },
                {
                    id: "recepti",
                    title: "Recepti",
                    href: "/profil/[username]/moji-recepti",
                    as: `/profil/${username}/moji-recepti`,
                },
                {
                    id: "slike",
                    title: "Slike",
                    href: "/profil/[username]/moje-slike",
                    as: `/profil/${username}/moje-slike`,
                },
                {
                    id: "spremljeni-recepti",
                    title: "Spremljeni recepti",
                    href: "/profil/[username]/spremljeni-recepti",
                    as: `/profil/${username}/spremljeni-recepti`,
                },
            ].filter(Boolean),
        [username, activeTrophyAwards],
    );
    if (!userId) {
        return null;
    }
    return (
        <UserProfileHeadStyled className={className} {...rest}>
            <div className="userProfile_content">
                <div className="userProfileContent_head">
                    <UserProfileAvatar
                        inline
                        className="userProfileContent_avatar"
                        userId={userId}
                        title={title}
                        label={label}
                    />

                    <div className="userProfileContent_btnWrapper">
                        <Button
                            onClick={handleFollowUser}
                            title={buttonTitle}
                            variant={buttonVariants.PRIMARY}
                            size={buttonSizes.NORMAL}
                        >
                            {buttonTitle}
                        </Button>
                    </div>
                </div>

                <UserProfileContent lead={lead} text={text}>
                    {trophiesEnabled && hasTrophiesList && activeTrophyAwards?.length > 0 && (
                        <UserProfileTrophy
                            trophies={activeTrophyAwards}
                            title="Moji trofeji"
                            linkTitle="Pogledaj sve trofeje"
                            linkUrl={trophiesLink}
                        />
                    )}
                </UserProfileContent>
            </div>

            {tabs && <UserProfileTabs tabs={tabs} />}
        </UserProfileHeadStyled>
    );
};

UserProfileHead.propTypes = {
    className: PropTypes.string,
    userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    label: PropTypes.string,
    title: PropTypes.string,
    lead: PropTypes.string,
    text: PropTypes.string,
    username: PropTypes.string,
    tabs: PropTypes.arrayOf(PropTypes.object),
    hasTrophiesList: PropTypes.bool,
    trophiesLink: PropTypes.string,
};

UserProfileHead.defaultProps = {
    className: undefined,
    userId: undefined,
    label: undefined,
    title: undefined,
    lead: undefined,
    text: undefined,
    username: undefined,
    tabs: undefined,
    hasTrophiesList: false,
    trophiesLink: undefined,
};

export default withErrorBoundary(UserProfileHead);
